import { isMorphNearMatch, normalizeToken } from './arabicMorph'
import { tokenizeWords } from './diffUtils'

const LEDGER_OCCUPANCY_MORPH_NEAR_COST = 0.35

type LedgerVarianceToken = {
  raw: string
  normalized: string
}

export type LedgerVarianceMetrics = {
  rowScore: number
  cellScores: number[]
  referenceIndex: number
}

export type LedgerVarianceMappedEntry = {
  witnessId: string
  text: string
}

const getVarianceTokens = (text: string): LedgerVarianceToken[] =>
  tokenizeWords(text)
    .map(raw => ({
      raw,
      normalized: normalizeToken(raw)
    }))
    .filter(token => Boolean(token.normalized))

const getLedgerPairVarianceScore = (leftTokens: LedgerVarianceToken[], rightTokens: LedgerVarianceToken[]): number => {
  const maxLength = Math.max(leftTokens.length, rightTokens.length)
  if (maxLength === 0) {
    return 0
  }

  const distanceMatrix: number[][] = Array.from(
    { length: leftTokens.length + 1 },
    () => Array(rightTokens.length + 1).fill(0)
  )

  for (let leftIndex = 0; leftIndex <= leftTokens.length; leftIndex += 1) {
    distanceMatrix[leftIndex][0] = leftIndex
  }
  for (let rightIndex = 0; rightIndex <= rightTokens.length; rightIndex += 1) {
    distanceMatrix[0][rightIndex] = rightIndex
  }

  for (let leftIndex = 1; leftIndex <= leftTokens.length; leftIndex += 1) {
    for (let rightIndex = 1; rightIndex <= rightTokens.length; rightIndex += 1) {
      const leftToken = leftTokens[leftIndex - 1]
      const rightToken = rightTokens[rightIndex - 1]
      const substitutionCost = leftToken.normalized === rightToken.normalized
        ? 0
        : isMorphNearMatch(leftToken.raw, rightToken.raw)
          ? LEDGER_OCCUPANCY_MORPH_NEAR_COST
          : 1
      distanceMatrix[leftIndex][rightIndex] = Math.min(
        distanceMatrix[leftIndex - 1][rightIndex] + 1,
        distanceMatrix[leftIndex][rightIndex - 1] + 1,
        distanceMatrix[leftIndex - 1][rightIndex - 1] + substitutionCost
      )
    }
  }

  const sequenceDrift = distanceMatrix[leftTokens.length][rightTokens.length] / maxLength
  const lengthDrift = Math.abs(leftTokens.length - rightTokens.length) / maxLength
  return Math.max(sequenceDrift, lengthDrift * 0.5)
}

const getLedgerVarianceQuantile = (scores: number[], quantile: number): number => {
  if (scores.length === 0) {
    return 0
  }
  const sorted = [...scores].sort((left, right) => left - right)
  const clampedQuantile = Math.max(0, Math.min(1, quantile))
  const position = (sorted.length - 1) * clampedQuantile
  const lowerIndex = Math.floor(position)
  const upperIndex = Math.ceil(position)
  if (lowerIndex === upperIndex) {
    return sorted[lowerIndex]
  }
  const progress = position - lowerIndex
  return sorted[lowerIndex] + ((sorted[upperIndex] - sorted[lowerIndex]) * progress)
}

export const getLedgerVarianceMetrics = (texts: string[]): LedgerVarianceMetrics => {
  const tokenized = texts.map(getVarianceTokens)

  if (tokenized.length === 0) {
    return {
      rowScore: 0,
      cellScores: [],
      referenceIndex: -1
    }
  }

  if (tokenized.length === 1) {
    return {
      rowScore: 0,
      cellScores: [0],
      referenceIndex: 0
    }
  }

  const distanceMatrix = tokenized.map(() => tokenized.map(() => 0))
  const totalDistances = tokenized.map(() => 0)
  for (let leftIndex = 0; leftIndex < tokenized.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < tokenized.length; rightIndex += 1) {
      const score = getLedgerPairVarianceScore(tokenized[leftIndex], tokenized[rightIndex])
      distanceMatrix[leftIndex][rightIndex] = score
      distanceMatrix[rightIndex][leftIndex] = score
      totalDistances[leftIndex] += score
      totalDistances[rightIndex] += score
    }
  }

  let medoidIndex = 0
  for (let index = 1; index < totalDistances.length; index += 1) {
    if (totalDistances[index] < totalDistances[medoidIndex]) {
      medoidIndex = index
    }
  }

  const cellScores = distanceMatrix.map((scores, index) => (index === medoidIndex ? 0 : scores[medoidIndex]))
  return {
    rowScore: getLedgerVarianceQuantile(cellScores, 0.75),
    cellScores,
    referenceIndex: medoidIndex
  }
}

export const buildLedgerVarianceCacheKey = (entries: LedgerVarianceMappedEntry[]): string => (
  entries
    .map(entry => `${entry.witnessId}\u241f${entry.text}`)
    .join('\u241e')
)
